/**
 * PostHog Analytics Sender
 *
 * Provides a unified analytics sender for all platforms (web, iOS, Android).
 * Holds a module-level PostHog instance reference set by the provider's
 * PostHogBridge component via setPostHogInstance().
 * All functions are no-ops when posthogInstance is null (graceful degradation).
 * @module features/analytics/utils/posthogSender
 */
import type { PostHog } from "posthog-react-native";
import { Platform } from "react-native";

import { validateEvent } from "./validator";
import type { TEventParameter } from "../types";
import { version as appVersion } from "../../../../package.json";
import { RAW_EXPO_PUBLIC_ENV } from "@/lib/env";

/** PostHog-compatible event properties record type. */
type CaptureProperties = Record<string, string | number | boolean | null>;

/**
 * Module-level PostHog instance, set by the provider bridge.
 * Null when PostHog is disabled or before the provider mounts.
 */
// eslint-disable-next-line functional/no-let -- Module-level singleton set by provider bridge
let posthogInstance: PostHog | null = null;

/**
 * Cached organization super-property context (SE-4009).
 *
 * Set once the session identity resolves (see
 * `features/auth/providers/CurrentUserSessionIdentity`) and re-registered
 * whenever the provider bridge (re)sets the PostHog instance, so an unmounted
 * or remounted bridge cannot silently drop `org_id` / `org_name` from events.
 * Holder object (not `let`) so the mutable slot is an object property.
 */
const organizationContextHolder: {
  current: {
    readonly org_id: string;
    readonly org_name: string | null;
  } | null;
} = { current: null };

/**
 * Resolves the environment name attached to analytics identity and events.
 * @returns The build's `EXPO_PUBLIC_ENV`, defaulting to `"localhost"` when unset
 */
export const getAnalyticsEnvironment = (): string =>
  // SE-7834: the RAW value. `env.EXPO_PUBLIC_ENV` fails closed to "production"
  // so authorization gates shut on an unreadable configuration; tagging events
  // that way would file local and CI traffic under production analytics, which
  // is the wrong direction for a data label. An unrecognized build keeps
  // reporting "localhost".
  RAW_EXPO_PUBLIC_ENV ?? "localhost";

/**
 * Sets the module-level PostHog instance from the React context bridge.
 * Called by PostHogBridge on mount/unmount.
 *
 * On set (non-null), registers PostHog super-properties so they are attached
 * to EVERY captured event without touching the per-event strict validators
 * (SE-5224). PostHog merges these into all subsequent `capture()` payloads at
 * the SDK level — they never flow through `validateEvent`, so the Yup schemas
 * stay closed while every event still carries the context:
 *
 * - `environment` — the build's `EXPO_PUBLIC_ENV`, defaulting to `"localhost"`.
 * - `$app_version` — web only (SE-4009). Native builds get `$app_version`
 *   from the SDK's native context, but web events ship it as null; registering
 *   the bundled `package.json` version fills the standard slot so version
 *   adoption is queryable across platforms. (Platform check is telemetry
 *   capability gating, not a layout decision.)
 * - `org_id` / `org_name` — re-registered from the cached organization
 *   context when a session identity already resolved (SE-4009).
 *
 * `register` is guarded for older/disabled SDK shapes that may not expose it.
 * @param instance - The PostHog instance or null to clear
 */
export const setPostHogInstance = (instance: PostHog | null): void => {
  posthogInstance = instance;

  if (instance && typeof instance.register === "function") {
    instance.register({
      environment: getAnalyticsEnvironment(),
      ...(Platform.OS === "web" ? { $app_version: appVersion } : {}),
      ...organizationContextHolder.current,
    });
  }
};

/**
 * Registers the resolved organization as `org_id` / `org_name` super-properties
 * so every subsequent event carries the org context (SE-4009).
 *
 * The context is cached module-level and re-registered by
 * {@link setPostHogInstance} if the provider bridge remounts, so the props
 * survive instance swaps. Cleared by {@link clearAnalyticsOrganizationContext}
 * and {@link resetAnalytics}.
 * @param orgId - The organization's unique identifier
 * @param orgName - The organization's display name, when known
 */
export const setAnalyticsOrganizationContext = (
  orgId: string,
  orgName?: string | null
): void => {
  organizationContextHolder.current = {
    org_id: orgId,
    org_name: orgName ?? null,
  };

  if (posthogInstance && typeof posthogInstance.register === "function") {
    posthogInstance.register({ ...organizationContextHolder.current });
  }
};

/**
 * Clears the organization super-properties (SE-4009).
 *
 * Called when a session resolves with no organization (e.g. the user left or
 * has not joined an org) so stale `org_id` / `org_name` never leak onto
 * subsequent events. `unregister` is guarded for SDK shapes without it.
 */
export const clearAnalyticsOrganizationContext = (): void => {
  organizationContextHolder.current = null;

  if (posthogInstance && typeof posthogInstance.unregister === "function") {
    posthogInstance.unregister("org_id");
    posthogInstance.unregister("org_name");
  }
};

/**
 * Sends an analytics event to PostHog.
 * @param eventName - The name of the event to log
 * @param params - Type-safe event parameters matching TEventParameter structure
 * @remarks
 * Events are validated before sending. No-op when PostHog instance is null.
 */
export async function sendAnalyticsEvent(
  eventName: string,
  params: TEventParameter
): Promise<void> {
  try {
    if (!posthogInstance) {
      return;
    }

    const isValid = await validateEvent(eventName, params);
    if (!isValid) {
      return;
    }
    posthogInstance.capture(eventName, params as CaptureProperties);
  } catch (error) {
    console.error(`Failed to log event: ${eventName}`, error);
  }
}

/**
 * Forwards a caught exception to PostHog as a `$exception` event.
 *
 * SE-5042: Sentry remains the authoritative crash/perf source of truth (full
 * stack traces, native crashes, source maps, replays). This forwards a
 * lightweight mirror to PostHog so client errors are queryable in product
 * analytics — attributable to the same person/session (PostHog `distinct_id`
 * and Sentry user `id` are both `me.id`) and usable in funnels/cohorts.
 *
 * No-op when the PostHog instance is null (graceful degradation); errors thrown
 * by the SDK are swallowed so reporting an error can never itself crash the app.
 * @param error - The error (or unknown thrown value) to forward
 * @param additionalProperties - Optional properties to attach to the `$exception` event
 */
export const captureException = (
  error: unknown,
  additionalProperties?: CaptureProperties
): void => {
  try {
    if (!posthogInstance) {
      return;
    }
    posthogInstance.captureException(error, additionalProperties);
  } catch (sendError) {
    console.error("Failed to forward $exception to PostHog", sendError);
  }
};

/**
 * Identifies a user in PostHog for cross-session tracking.
 * @param userId - Unique user identifier
 * @param properties - Optional user properties to set
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, string | number | boolean | null>
): void => {
  if (!posthogInstance) {
    return;
  }
  posthogInstance.identify(userId, properties);
  // SE-5123: PostHog caches flag evaluations against the anonymous distinct_id.
  // After identify() the cached values are stale for org-targeted flags;
  // force a re-evaluation so role-gated nav items resolve for this user.
  // Fire-and-forget — identifyUser must stay synchronous and never throw.
  posthogInstance.reloadFeatureFlagsAsync().catch(reloadError => {
    console.error("PostHog reloadFeatureFlagsAsync failed", reloadError);
  });
};

/**
 * Associates the current user with a group (e.g., organization).
 * @param groupType - The type of group (e.g., "organization")
 * @param groupId - The group's unique identifier
 * @param properties - Optional group properties to set
 */
export const setAnalyticsGroup = (
  groupType: string,
  groupId: string,
  properties?: Record<string, string | number | boolean | null>
): void => {
  if (!posthogInstance) {
    return;
  }
  posthogInstance.group(groupType, groupId, properties);
};

/**
 * Resets the PostHog identity, clearing the current user and group associations.
 * Call on sign-out to prevent event attribution to the previous user.
 *
 * Also drops the cached organization super-property context (SE-4009) so the
 * previous session's `org_id` / `org_name` cannot be re-registered into the
 * next session by a provider bridge remount.
 */
export const resetAnalytics = (): void => {
  organizationContextHolder.current = null;

  if (!posthogInstance) {
    return;
  }
  posthogInstance.reset();
};
